import type { SingleProductData } from "@repo/apis/single-product";
import type { StockPriceMappedProduct } from "@/utils/map-product-data";
import type { StructuredData, StructuredDataProduct } from "./types";

export const PRODUCT_STRUCTURED_DATA_ID = "product-structured-data";

const stripHtml = (html: string): string => {
  const withSpaces = html
    .replace(/<li>/g, "")
    .replace(/<\/li>/g, " ")
    .replace(/<br\s*\/?>/gi, " ");

  const withoutTags = withSpaces.replace(/<[^>]*>/g, "");

  const decoded = withoutTags
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

  return decoded
    .replace(/\s+/g, " ")
    .replace(/\s+([.,])/g, "$1")
    .trim();
};

const isStockPriceMappedProduct = (
  product: SingleProductData | StockPriceMappedProduct,
): product is StockPriceMappedProduct => {
  return (
    "variants" in product &&
    Array.isArray(product.variants) &&
    product.variants.length > 0 &&
    product.variants[0] !== undefined &&
    "price" in product.variants[0]
  );
};

export const createStructuredData = (
  product: SingleProductData | StockPriceMappedProduct,
  url: string,
): StructuredData => {
  const formattedUrl = url.startsWith("http") ? url : `https://${url}`;
  const defaultVariant = product.variants?.[0];

  if (!defaultVariant) {
    return {
      "@context": "https://schema.org/",
      "@graph": [
        {
          "@type": "Product",
          name: product.name,
          sku: "",
          color: "",
          image: product.media?.images[0]?.url || "",
          brand: {
            "@type": "Brand",
            name: product.brand?.name || "Topshop",
          },
          description: stripHtml(product.description),
          productID: product.id,
          url: formattedUrl,
          offers: isStockPriceMappedProduct(product) ? [] : {},
        },
      ],
    };
  }

  const structuredProduct: StructuredDataProduct = {
    "@type": "Product",
    name: product.name,
    sku: defaultVariant.sku,
    color: defaultVariant.colour,
    image: product.media?.images[0]?.url || "",
    brand: {
      "@type": "Brand",
      name: product.brand?.name || "Topshop",
    },
    description: stripHtml(product.description),
    productID: product.id,
    url: formattedUrl,
    offers: isStockPriceMappedProduct(product)
      ? product.variants.map((variant) => ({
          "@type": "Offer",
          price: variant.price.current.value,
          priceCurrency: variant.price.currency,
          itemOffered: {
            "@type": "IndividualProduct",
            name: product.name,
            sku: variant.sku,
            color: variant.colour,
            image: product.media?.images[0]?.url || "",
            brand: {
              "@type": "Brand",
              name: product.brand?.name || "Topshop",
            },
          },
          seller: {
            "@type": "Organization",
            name: "Topshop",
          },
          availability: variant.isInStock
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          itemCondition: "https://schema.org/NewCondition",
        }))
      : {},
  };

  if ("rating" in product && product.rating) {
    structuredProduct.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: product.rating.averageOverallRating,
      ratingCount: product.rating.totalReviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return {
    "@context": "https://schema.org/",
    "@graph": [structuredProduct],
  };
};
